<template>
    <div>
        <div
            v-if="!isReady || isLoading"
            class="grid grid-cols-1 gap-4 md:grid-cols-6"
        >
            <!-- Card Number -->
            <div class="md:col-span-4">
                <div class="mb-1">Card number</div>
                <prime-skeleton class="min-h-[36px]" />
            </div>

            <!-- Expiration Date -->
            <div class="md:col-span-1">
                <div class="mb-1">Exp. date</div>
                <prime-skeleton class="min-h-[36px]" />
            </div>

            <!-- CVV -->
            <div class="md:col-span-1">
                <div class="mb-1">CVV</div>
                <prime-skeleton class="min-h-[36px]" />
            </div>
        </div>

        <windcave-hosted-fields
            v-if="isReady && data !== undefined"
            ref="windcaveHostedFieldsRef"
            :key="uniqueQueryId"
            :payment-source-id="props.paymentSourceId"
            :payment-source-data="data"
            @payment-method="$emit('paymentMethod', $event)"
            @transaction-error="handleTransactionError"
            @validation-error="handleValidationError"
        />

        <div
            v-if="transactionError || validationError || isError"
            class="mt-4 flex items-center justify-between gap-4"
        >
            <div class="font-bold text-danger-600">
                {{ transactionError || validationError || isError || '&nbsp;' }}
            </div>

            <prime-button
                text
                class="!p-1"
                label="Refresh Payment Form"
                :loading="isProcessing"
                @click="handleRefreshPaymentForm"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, toRef } from 'vue'
import { graphql } from '~/resources/graphql'
import { type TokenPaymentMethod } from '~/components/Checkout/Sections/Payment/payment-method'
import { useRpQuery } from '~/composables/graphql'
import { useWindcaveScripts } from '~/components/Payments/use-windcave-scripts'
import WindcaveHostedFields from '~/components/Payments/WindcaveHostedFields.vue'
import { useQuery } from '@tanstack/vue-query'
import PrimeSkeleton from 'primevue/skeleton'
import { randomString } from '~/composables/support/util'
import PrimeButton from 'primevue/button'

const props = defineProps<{
    paymentSourceId: string
    refreshButtonAboveForm?: boolean
    isProcessing?: boolean
}>()

defineEmits<{
    (e: 'paymentMethod', payload: TokenPaymentMethod | undefined): void
}>()

const { loadWindcaveHostedScripts } = useWindcaveScripts()
const isReady = ref(false)
loadWindcaveHostedScripts().then(() => {
    isReady.value = true
})

const transactionError = ref<string>()
const validationError = ref<string>()

const uniqueQueryId = ref(randomString())
const rpQuery = useRpQuery()
// We use vue-query here so we get retries on the initial load, but we don't
// want to cache the data
const { data, isLoading, isError } = useQuery({
    // Never consider the data stale
    staleTime: Infinity,
    // Keep data in cache forever
    gcTime: Infinity,
    // Disable automatic refetches
    refetchOnMount: false, // won't refetch when component mounts
    refetchOnWindowFocus: false, // won't refetch when window refocuses
    refetchOnReconnect: false, // won't refetch when reconnecting
    refetchInterval: false, // disables polling
    queryKey: [
        'WindcaveSetupFormQuery',
        uniqueQueryId,
        toRef(props, 'paymentSourceId')
    ],
    queryFn: async () => {
        const response = await rpQuery(
            graphql(/* GraphQL */ `
                query WindcaveSetupFormQuery($paymentSourceId: ID!) {
                    node(id: $paymentSourceId) {
                        __typename
                        ... on PaymentSource {
                            data {
                                __typename
                                ... on PaymentSourceCreditCardWindcave {
                                    session {
                                        url
                                        supportedCards
                                        sessionId
                                    }
                                }
                            }
                        }
                    }
                }
            `),
            {
                paymentSourceId: props.paymentSourceId
            }
        )

        if (
            response.node === null ||
            response.node.__typename !== 'PaymentSource' ||
            response.node.data === null ||
            response.node.data.__typename !== 'PaymentSourceCreditCardWindcave'
        ) {
            throw new Error()
        }

        // Save session data
        return response.node.data.session
    }
})

function handleTransactionError(message: string) {
    transactionError.value = message
    uniqueQueryId.value = randomString()
}

function handleValidationError(message: string) {
    validationError.value = message
}

const windcaveHostedFieldsRef = ref<InstanceType<typeof WindcaveHostedFields>>()
async function submitPaymentMethod() {
    transactionError.value = undefined
    validationError.value = undefined
    if (windcaveHostedFieldsRef.value !== undefined) {
        await windcaveHostedFieldsRef.value.submitPaymentMethod()
    }
}

function handleRefreshPaymentForm() {
    transactionError.value = undefined
    validationError.value = undefined
    uniqueQueryId.value = randomString()
}

defineExpose({
    submitPaymentMethod
})
</script>
